/**
 * Hook for monitoring and optimizing person photo performance
 * Provides utilities for tracking loading times, cache efficiency, and optimization metrics
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  globalImageCache, 
  globalPerformanceMonitor, 
  createOptimizedImageLoader,
  ImageSizeConfig,
  getImageSizeForContext
} from '../utils/imageOptimization';
import { PersonSummaryWithPhoto } from '../types';
import { getConfig } from '../config';

export interface PersonPhotoPerformanceMetrics {
  totalImages: number;
  loadedImages: number;
  failedImages: number;
  averageLoadTime: number;
  cacheHitRate: number;
  memoryUsage: number;
  optimizationEnabled: boolean;
}

export interface PersonPhotoLoadingState {
  isLoading: boolean;
  loadedCount: number;
  totalCount: number;
  progress: number;
  errors: string[];
}

/**
 * Hook for monitoring person photo performance across the application
 */
export const usePersonPhotoPerformance = () => {
  const [metrics, setMetrics] = useState<PersonPhotoPerformanceMetrics>({
    totalImages: 0,
    loadedImages: 0,
    failedImages: 0,
    averageLoadTime: 0,
    cacheHitRate: 0,
    memoryUsage: 0,
    optimizationEnabled: true,
  });

  const updateMetrics = useCallback(() => {
    const cacheStats = globalImageCache.getStats();
    const performanceMetrics = globalPerformanceMonitor.getMetrics();
    
    const loadedImages = performanceMetrics.filter(m => !m.error).length;
    const failedImages = performanceMetrics.filter(m => m.error).length;
    const totalImages = performanceMetrics.length;

    setMetrics({
      totalImages,
      loadedImages,
      failedImages,
      averageLoadTime: globalPerformanceMonitor.getAverageLoadTime(),
      cacheHitRate: globalPerformanceMonitor.getCacheHitRate(),
      memoryUsage: cacheStats.estimatedMemoryUsage,
      optimizationEnabled: getConfig().enableLazyLoading,
    });
  }, []);

  const resetMetrics = useCallback(() => {
    globalPerformanceMonitor.clear();
    globalImageCache.clear();
    updateMetrics();
  }, [updateMetrics]);

  const getDetailedMetrics = useCallback(() => {
    return {
      cache: globalImageCache.getStats(),
      performance: globalPerformanceMonitor.getMetrics(),
      summary: metrics,
    };
  }, [metrics]);

  // Update metrics periodically
  useEffect(() => {
    const interval = setInterval(updateMetrics, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, [updateMetrics]);

  return {
    metrics,
    updateMetrics,
    resetMetrics,
    getDetailedMetrics,
  };
};

/**
 * Hook for optimized batch loading of person photos
 */
export const usePersonPhotoBatchLoader = () => {
  // Safely get query client, return null if not available (e.g., in tests)
  let queryClient: any = null;
  try {
    queryClient = useQueryClient();
  } catch (error) {
    // QueryClient not available, continue without it
  }

  const loaderRef = useRef(createOptimizedImageLoader({
    enableMonitoring: true,
    cache: { enablePreloading: true },
  }));

  const preloadPersonPhotos = useCallback(async (
    people: PersonSummaryWithPhoto[],
    context: 'overlay' | 'grid' | 'detail' | 'thumbnail' = 'overlay',
    size?: 'small' | 'medium' | 'large'
  ) => {
    const imageConfig = getImageSizeForContext(context, size);
    const urls = people
      .filter(person => person.imageUrl)
      .map(person => person.imageUrl!);

    if (urls.length === 0) return;

    try {
      await loaderRef.current.preloadImages(urls, imageConfig);
      
      // Cache the preloaded data in React Query for faster access (if available)
      if (queryClient) {
        const cacheKey = ['person-photos-batch', context, size];
        queryClient.setQueryData(cacheKey, {
          people,
          preloadedAt: Date.now(),
          imageConfig,
        });
      }
    } catch (error) {
      console.warn('[PersonPhotoBatchLoader] Preloading failed:', error);
    }
  }, [queryClient]);

  const getBatchLoadingStats = useCallback(() => {
    return loaderRef.current.getStats();
  }, []);

  const clearBatchCache = useCallback(() => {
    loaderRef.current.cleanup();
    loaderRef.current = createOptimizedImageLoader({
      enableMonitoring: true,
      cache: { enablePreloading: true },
    });
  }, []);

  return {
    preloadPersonPhotos,
    getBatchLoadingStats,
    clearBatchCache,
  };
};

/**
 * Hook for tracking loading state of person photos in a component
 */
export const usePersonPhotoLoadingState = (people: PersonSummaryWithPhoto[]) => {
  const [loadingState, setLoadingState] = useState<PersonPhotoLoadingState>({
    isLoading: false,
    loadedCount: 0,
    totalCount: 0,
    progress: 0,
    errors: [],
  });

  const trackingRef = useRef(new Map<string, boolean>());

  const updateLoadingState = useCallback(() => {
    const totalCount = people && Array.isArray(people) ? people.filter(p => p.imageUrl).length : 0;
    const loadedCount = Array.from(trackingRef.current.values()).filter(Boolean).length;
    const progress = totalCount > 0 ? (loadedCount / totalCount) * 100 : 100;
    const isLoading = loadedCount < totalCount;

    setLoadingState(prev => ({
      ...prev,
      isLoading,
      loadedCount,
      totalCount,
      progress,
    }));
  }, [people]);

  const markImageLoaded = useCallback((personId: string, success: boolean, error?: string) => {
    trackingRef.current.set(personId, success);
    
    if (!success && error) {
      setLoadingState(prev => ({
        ...prev,
        errors: [...prev.errors.filter(e => !e.includes(personId)), `${personId}: ${error}`],
      }));
    }
    
    updateLoadingState();
  }, [updateLoadingState]);

  const resetLoadingState = useCallback(() => {
    trackingRef.current.clear();
    setLoadingState({
      isLoading: false,
      loadedCount: 0,
      totalCount: 0,
      progress: 0,
      errors: [],
    });
  }, []);

  // Initialize tracking when people change
  useEffect(() => {
    trackingRef.current.clear();
    if (people && Array.isArray(people)) {
      people.forEach(person => {
        if (person.imageUrl) {
          trackingRef.current.set(person.id, false);
        }
      });
    }
    updateLoadingState();
  }, [people, updateLoadingState]);

  return {
    loadingState,
    markImageLoaded,
    resetLoadingState,
  };
};

/**
 * Hook for adaptive image quality based on network conditions
 */
export const useAdaptiveImageQuality = () => {
  const [networkQuality, setNetworkQuality] = useState<'high' | 'medium' | 'low'>('high');
  const [adaptiveConfig, setAdaptiveConfig] = useState<ImageSizeConfig>({
    width: 60,
    height: 60,
    quality: 85,
  });

  // Monitor network conditions
  useEffect(() => {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      
      const updateNetworkQuality = () => {
        const effectiveType = connection.effectiveType;
        
        if (effectiveType === '4g') {
          setNetworkQuality('high');
        } else if (effectiveType === '3g') {
          setNetworkQuality('medium');
        } else {
          setNetworkQuality('low');
        }
      };

      updateNetworkQuality();
      connection.addEventListener('change', updateNetworkQuality);
      
      return () => {
        connection.removeEventListener('change', updateNetworkQuality);
      };
    }
  }, []);

  // Update adaptive configuration based on network quality
  useEffect(() => {
    const qualityConfigs = {
      high: { width: 80, height: 80, quality: 90 },
      medium: { width: 60, height: 60, quality: 75 },
      low: { width: 40, height: 40, quality: 60 },
    };

    setAdaptiveConfig(qualityConfigs[networkQuality]);
  }, [networkQuality]);

  const getAdaptiveImageConfig = useCallback((
    baseConfig: ImageSizeConfig
  ): ImageSizeConfig => {
    return {
      ...baseConfig,
      quality: Math.min(baseConfig.quality || 85, adaptiveConfig.quality || 85),
    };
  }, [adaptiveConfig]);

  return {
    networkQuality,
    adaptiveConfig,
    getAdaptiveImageConfig,
  };
};

/**
 * Hook for progressive image enhancement
 * Loads low-quality images first, then enhances with high-quality versions
 */
export const useProgressiveImageEnhancement = () => {
  const [enhancementEnabled, setEnhancementEnabled] = useState(true);
  
  const createProgressiveLoader = useCallback((
    url: string,
    onLowQualityLoad?: (img: HTMLImageElement) => void,
    onHighQualityLoad?: (img: HTMLImageElement) => void
  ) => {
    if (!enhancementEnabled) {
      // Load only high-quality image
      const img = new Image();
      img.onload = () => onHighQualityLoad?.(img);
      img.src = url;
      return;
    }

    // Load low-quality version first
    // Low quality config for progressive loading
    // const lowQualityConfig = { width: 20, height: 20, quality: 30 };
    const lowQualityUrl = url; // In a real implementation, this would be optimized
    
    const lowQualityImg = new Image();
    lowQualityImg.onload = () => {
      onLowQualityLoad?.(lowQualityImg);
      
      // Then load high-quality version
      const highQualityImg = new Image();
      highQualityImg.onload = () => onHighQualityLoad?.(highQualityImg);
      highQualityImg.src = url;
    };
    lowQualityImg.src = lowQualityUrl;
  }, [enhancementEnabled]);

  const toggleEnhancement = useCallback(() => {
    setEnhancementEnabled(prev => !prev);
  }, []);

  return {
    enhancementEnabled,
    createProgressiveLoader,
    toggleEnhancement,
  };
};

/**
 * Hook for memory-efficient image management
 */
export const useImageMemoryManagement = () => {
  const [memoryUsage, setMemoryUsage] = useState(0);
  const [memoryLimit] = useState(50 * 1024 * 1024); // 50MB limit
  
  const checkMemoryUsage = useCallback(() => {
    const stats = globalImageCache.getStats();
    setMemoryUsage(stats.estimatedMemoryUsage);
    
    // Clear cache if memory usage is too high
    if (stats.estimatedMemoryUsage > memoryLimit) {
      console.warn('[ImageMemoryManagement] Memory limit exceeded, clearing cache');
      globalImageCache.clear();
      setMemoryUsage(0);
    }
  }, [memoryLimit]);

  const forceCleanup = useCallback(() => {
    globalImageCache.clear();
    globalPerformanceMonitor.clear();
    setMemoryUsage(0);
  }, []);

  const getMemoryStats = useCallback(() => {
    return {
      current: memoryUsage,
      limit: memoryLimit,
      usage: (memoryUsage / memoryLimit) * 100,
      shouldCleanup: memoryUsage > memoryLimit * 0.8, // Warn at 80%
    };
  }, [memoryUsage, memoryLimit]);

  // Check memory usage periodically
  useEffect(() => {
    const interval = setInterval(checkMemoryUsage, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, [checkMemoryUsage]);

  return {
    memoryUsage,
    memoryLimit,
    checkMemoryUsage,
    forceCleanup,
    getMemoryStats,
  };
};